// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useMemo } from 'react';
import {
  ONBOARDING_QUESTIONS,
  OnboardingQuestionBlock,
} from '@shared/advisory/account-workflow/types';
import type {
  InvestmentObjectivesFormData,
  FinancialKnowledgeFormData,
  InvestmentExperienceFormData,
  FinancialSituationFormData,
  SustainabilityFormData,
} from '@shared/advisory/account-workflow/types';
import { useTranslation } from 'react-i18next';

export interface QuestionnaireAnswersAccordionProps {
  investmentObjectives: InvestmentObjectivesFormData | undefined;
  financialKnowledge: FinancialKnowledgeFormData | undefined;
  investmentExperience: InvestmentExperienceFormData | undefined;
  financialSituation: FinancialSituationFormData | undefined;
  sustainability: SustainabilityFormData | undefined;
}

export type FormData =
  | InvestmentObjectivesFormData
  | FinancialKnowledgeFormData
  | InvestmentExperienceFormData
  | FinancialSituationFormData
  | SustainabilityFormData;

export const useQuestionLayout = ({
  investmentObjectives,
  financialKnowledge,
  investmentExperience,
  financialSituation,
  sustainability,
}: QuestionnaireAnswersAccordionProps) => {
  const { t } = useTranslation();

  const formData = useMemo(
    () =>
      new Map<OnboardingQuestionBlock, FormData | undefined>([
        [OnboardingQuestionBlock.InvestmentObjectives, investmentObjectives],
        [OnboardingQuestionBlock.FinancialKnowledge, financialKnowledge],
        [OnboardingQuestionBlock.InvestmentExperience, investmentExperience],
        [OnboardingQuestionBlock.FinancialSituation, financialSituation],
        [OnboardingQuestionBlock.Sustainability, sustainability],
      ]),
    [
      investmentObjectives,
      financialKnowledge,
      investmentExperience,
      financialSituation,
      sustainability,
    ],
  );

  const blocks = useMemo(
    () =>
      Array.from(ONBOARDING_QUESTIONS.entries(), ([block, questions]) => {
        const filteredQuestions =
          block === OnboardingQuestionBlock.Sustainability
            ? questions.filter(q => q.id === 'q_sustainability_preferences')
            : questions;

        return {
          id: block,
          title: t(`onboarding-blocks.${block}`, {
            ns: 'components',
          }),
          questions: filteredQuestions.map(question => ({
            id: question.id,
            label: t(...question.label),
            answerValue: t(
              ...(question.options.find(
                option =>
                  option.value ===
                  formData.get(block)?.[question.id as keyof FormData],
              )?.label ?? [
                'onboarding-process-overview.result-and-signature.test-answers.not-specified',
                { ns: 'components' },
              ]),
            ),
          })),
        };
      }),
    [formData, t],
  );

  return {
    blocks,
  };
};
